import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { FAQ_CATEGORIES } from "@/data/site";

export default function FAQ() {
  const { t } = useTranslation();
  const location = useLocation();
  const [activeCat, setActiveCat] = useState(FAQ_CATEGORIES[0].id);
  const [openItem, setOpenItem] = useState(undefined);
  const current = FAQ_CATEGORIES.find((c) => c.id === activeCat);

  // Hash deep-link · #<faq-id> jumps to the category that owns it,
  // expands the matching accordion item, and scrolls it into view.
  // Hit from GlobalSearch results (e.g. /faq#general_3).
  useEffect(() => {
    const raw = (location.hash || "").replace(/^#/, "");
    if (!raw) return;
    for (const cat of FAQ_CATEGORIES) {
      const idx = cat.items.findIndex((it) => it.id === raw);
      if (idx >= 0) {
        setActiveCat(cat.id);
        setOpenItem(`item-${idx}`);
        // Defer scroll to next paint so the accordion is mounted.
        setTimeout(() => {
          const el = document.querySelector(`[data-testid="faq-item-${cat.id}-${idx}"]`);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 120);
        return;
      }
    }
  }, [location.hash]);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-14 lg:py-28 overflow-hidden">
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <div className="pill-mono inline-flex mb-4">{t("faq.pill")}</div>
          <h2 className="font-display text-3xl lg:text-4xl font-medium tracking-tight mb-3">
            {t("faq.title")} <span className="text-gradient-brand font-semibold">{t("faq.titleAccent")}</span>
          </h2>
          <p className="text-[color:var(--text-2)] text-sm lg:text-base font-light">
            {t("faq.subtitle")}
          </p>
        </motion.div>

        {/* Category tabs · clearly clickable, smaller, with hover ring */}
        <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="faq-category-tabs" role="tablist">
          {FAQ_CATEGORIES.map((cat) => {
            const active = activeCat === cat.id;
            return (
              <button
                key={cat.id}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={`faq-cat-${cat.id}`}
                onClick={() => { setActiveCat(cat.id); setOpenItem(undefined); }}
                className={`group relative px-3.5 py-2 rounded-lg text-xs lg:text-sm transition-all cursor-pointer
                  ${active
                    ? "bg-gradient-to-br from-brand to-brand-deep text-[color:var(--selection-fg)] shadow-[0_1px_4px_color-mix(in_srgb,var(--brand)_6%,transparent)]"
                    : "bg-[color:var(--surface)] border border-[color:var(--line)] text-[color:var(--text-2)] hover:border-brand/60 hover:text-[color:var(--text-1)]"}`}
              >
                <span className="font-medium">{t(`faq.cat_${cat.id}`, cat.label)}</span>
                <span className={`font-num text-[12px] ml-2 px-1.5 py-0.5 rounded-md ${active ? "bg-black/20" : "bg-[color:var(--line)]"}`}>
                  {cat.items.length}
                </span>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeCat}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
          >
            <Accordion type="single" collapsible value={openItem} onValueChange={setOpenItem} className="space-y-2.5" data-testid="faq-accordion">
              {current.items.map((f, i) => (
                <motion.div
                  key={`${activeCat}-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <AccordionItem
                    value={`item-${i}`}
                    className="card-beam border-0 px-5 lg:px-6 data-[state=open]:bg-[color:var(--surface)]"
                    data-testid={`faq-item-${activeCat}-${i}`}
                  >
                    <AccordionTrigger className="font-display text-left text-[color:var(--text-1)] text-sm lg:text-[15px] font-medium hover:no-underline py-4">
                      {t(`faqQ.${f.id}`, f.q)}
                    </AccordionTrigger>
                    <AccordionContent className="text-[color:var(--text-2)] text-sm leading-relaxed pb-4 font-light">
                      {t(`faqA.${f.id}`, f.a)}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
